// src/hooks/useBookmarks.ts
import { useState, useEffect, useCallback, useMemo } from 'react';

const STORAGE_KEY = 'fingerstyle_bookmarks';

interface BookmarkStore {
  version: 1;
  bookmarkedSlugs: string[];
  updatedAt: string;
}

interface UseBookmarksReturn {
  bookmarkedSlugs: string[];
  isBookmarked: (slug: string) => boolean;
  toggleBookmark: (slug: string) => void;
  clearAll: () => void;
}

function loadFromStorage(): string[] {
  try {
    const stored = localStorage.getItem(STORAGE_KEY);
    if (!stored) return [];
    const data: BookmarkStore = JSON.parse(stored);
    if (data.version === 1 && Array.isArray(data.bookmarkedSlugs)) {
      return data.bookmarkedSlugs;
    }
    return [];
  } catch {
    return [];
  }
}

function saveToStorage(slugs: string[]): void {
  const store: BookmarkStore = {
    version: 1,
    bookmarkedSlugs: slugs,
    updatedAt: new Date().toISOString(),
  };
  localStorage.setItem(STORAGE_KEY, JSON.stringify(store));
}

export function useBookmarks(): UseBookmarksReturn {
  const [bookmarkedSlugs, setBookmarkedSlugs] = useState<string[]>(() => loadFromStorage());

  useEffect(() => {
    saveToStorage(bookmarkedSlugs);
  }, [bookmarkedSlugs]);

  const bookmarkedSet = useMemo(() => new Set(bookmarkedSlugs), [bookmarkedSlugs]);
  const isBookmarked = useCallback(
    (slug: string) => bookmarkedSet.has(slug),
    [bookmarkedSet]
  );

  const toggleBookmark = useCallback((slug: string) => {
    setBookmarkedSlugs((prev) =>
      prev.includes(slug)
        ? prev.filter((s) => s !== slug)
        : [...prev, slug]
    );
  }, []);

  const clearAll = useCallback(() => {
    setBookmarkedSlugs([]);
  }, []);

  return {
    bookmarkedSlugs,
    isBookmarked,
    toggleBookmark,
    clearAll,
  };
}
